import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCheckoutStore } from "../store/checkout-store";
import { useCart, type UseCartReturn } from "@/features/cart/hooks/use-cart";
import { useGetAllCart } from "@/api/hooks/use-query-api";
import useAuthStore from "@/store/use-auth-store";
import { formatPrice as formatPriceUtil } from "@/features/orders/utils/format-price";
import type { CartItem } from "@/features/cart/store/cart-store";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import type { CartMerchant, CartProductImage } from "@/api/models/api-types";

/**
 * Checkout'a özel sepet hook'u.
 *
 * - `buyNowOfferId` set ise: global cart store'a dokunmadan, TanStack Query ile
 *   sadece o teklife ait veriyi çeker. Header ve diğer bileşenler etkilenmez.
 * - `buyNowOfferId` set değilse: normal `useCart()` döner.
 */
export const useCheckoutCart = (): UseCartReturn => {
  const { buyNowOfferId, buyNowQuantity } = useCheckoutStore();
  const regularCart = useCart();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const userCurrency = useAuthStore((state) => state.user?.currency) || "TRY";
  const { t } = useTranslation();
  const { error: showError } = useToast();

  // Sadece buy-now modunda TanStack Query ile veri çek (offerId + quantity)
  const {
    data: buyNowCartData,
    isLoading: isBuyNowLoading,
    error: buyNowQueryError,
  } = useGetAllCart(
    buyNowOfferId ?? undefined,
    buyNowOfferId ? (buyNowQuantity ?? 1) : undefined,
  );

  // Buy-now modunda stok yoksa API hata döner - kullanıcıyı bildir
  useEffect(() => {
    if (!buyNowOfferId || !buyNowQueryError) return;
    const errMsg =
      (buyNowQueryError as any)?.response?.data?.message ||
      (buyNowQueryError as any)?.message ||
      t("cart.buyNowUnavailable");
    showError(errMsg, {
      title: t("cart.cartChangedTitle"),
      duration: 6000,
      position: "bottom-right",
    });
  }, [buyNowOfferId, buyNowQueryError, t, showError]);

  const formatPrice = useMemo(
    () => (price: number) => formatPriceUtil(price, userCurrency),
    [userCurrency],
  );

  // Buy-now modunda değilsek normal cart'ı döndür
  const buyNowMerchants: CartMerchant[] | undefined = buyNowCartData?.data?.merchants;
  const buyNowCalculation = buyNowCartData?.data?.calculation;

  const buyNowItems = useMemo((): CartItem[] => {
    if (!buyNowMerchants) return [];
    return buyNowMerchants.flatMap((merchant) =>
      merchant.items.map((item) => {
        const primaryImage = item.offer.images?.find(
          (img: CartProductImage) => img.isPrimary,
        );
        const firstImage = item.offer.images?.[0];
        const imageUrl = primaryImage?.imageUrl || firstImage?.imageUrl || "";
        const finalPrice = item.offer.campaignPrice || item.offer.salePrice;
        const originalPrice = item.offer.salePrice;

        return {
          _id: item._id,
          productId: item.offer.productId,
          title: item.offer.name,
          description: item.offer.productName,
          type: "product",
          file: imageUrl,
          price: finalPrice,
          quantity: item.quantity,
          userId: "",
          isDeleted: false,
          createdAt: "",
          updatedAt: "",
          vendor: {
            id: merchant.merchantId,
            name: merchant.merchantName,
            logo: item.offer.sellerLogoUrl || "",
            slug: merchant.merchantSeoName,
          },
          metadata: {
            axis1Name: item.offer.axis1Name,
            axis2Name: item.offer.axis2Name,
            a1Value: item.offer.a1Value,
            a2Value: item.offer.a2Value,
            originalPrice,
            discountPct: item.offer.campaignPrice
              ? Math.round(
                  ((originalPrice - item.offer.campaignPrice) / originalPrice) *
                    100,
                )
              : 0,
            stockCount: item.offer.stockCount,
            inStock: item.offer.inStock,
            brand: item.offer.brandName,
            seller: item.offer.sellerName,
            size: item.offer.a1Value || item.offer.a2Value,
            color: item.offer.a2Value,
          },
          priceFormatted: formatPriceUtil(finalPrice, userCurrency),
          totalPrice: finalPrice * item.quantity,
        } as CartItem;
      }),
    );
  }, [buyNowMerchants, userCurrency]);

  const buyNowVendorGroups = useMemo(() => {
    if (!buyNowMerchants) return [];
    return buyNowMerchants.map((merchant) => ({
      vendorId: merchant.merchantId,
      vendorName: merchant.merchantName,
      vendorLogo: merchant.items[0]?.offer.sellerLogoUrl,
      items: merchant.items,
      itemCount: merchant.items.length,
      subtotal: merchant.calculation.subTotal,
      shipping: merchant.calculation.cargo,
      total: merchant.calculation.total,
    }));
  }, [buyNowMerchants]);

  const updateBuyNowQuantity = useCallback(
    async (_cartId: string, quantity: number) => {
      if (quantity <= 0) {
        navigate("/cart", { replace: true });
        return;
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("quantity", String(quantity));
          return next;
        },
        { replace: true },
      );
    },
    [navigate, setSearchParams],
  );

  const removeBuyNowItem = useCallback(async () => {
    navigate("/cart", { replace: true });
  }, [navigate]);

  // Buy-now modu: global store'a dokunmadan TanStack Query verisi kullan
  if (buyNowOfferId) {
    const subtotal = buyNowCalculation?.subTotal ?? 0;
    const shipping = buyNowCalculation?.cargo ?? 0;
    const totalPrice = buyNowCalculation?.total ?? 0;
    const cashback = buyNowCalculation?.cashback ?? 0;
    const tax = subtotal - subtotal / 1.18;
    const isEmpty = buyNowItems.length === 0;

    const buyNowError = buyNowQueryError
      ? (buyNowQueryError as any)?.response?.data?.message ||
        (buyNowQueryError as any)?.message ||
        t("cart.buyNowUnavailable")
      : null;

    return {
      items: buyNowItems,
      totalItems: buyNowItems.reduce((sum, item) => sum + item.quantity, 0),
      totalPrice,
      subtotal,
      tax,
      shipping,
      cashback,
      isLoading: isBuyNowLoading,
      error: buyNowError,
      isEmpty,
      vendorGroups: buyNowVendorGroups,
      addToCart: regularCart.addToCart,
      removeFromCart: removeBuyNowItem,
      updateQuantity: updateBuyNowQuantity,
      clearCart: regularCart.clearCart,
      getCartItem: regularCart.getCartItem,
      getCartItemByProductId: regularCart.getCartItemByProductId,
      isInCart: regularCart.isInCart,
      getItemQuantity: regularCart.getItemQuantity,
      formatPrice,
      getFreeShippingRemaining: () => Math.max(0, 500 - subtotal),
      isFreeShippingEligible: () => subtotal >= 500,
      loadCart: regularCart.loadCart,
    };
  }

  // Normal mod: global store'dan oku
  return regularCart;
};
